import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import style from './GiftsPage.module.css';

import logoAr from '../../assets/images/logoAr.png';
import logoEn from '../../assets/images/logoEn.png';
import boltLogo from '../../assets/logos/bolt.jpg';
import mounershLogo from '../../assets/logos/mounersh3d.png';

import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";

function GiftsPage() {
    const navigate = useNavigate();
    const [giftsList, setGiftsList] = useState([]);
    const [isAddGift, setIsAddGift] = useState(false);
    const [editingGiftId, setEditingGiftId] = useState(null);
    const [newGift, setNewGift] = useState({
        name: "",
        level: 1,
        description: ""
    });

    useEffect(() => {
        loadGifts();
    }, []);

    // --- LOAD GIFTS FROM FIRESTORE ---
    async function loadGifts() {
        try {
            const q = query(collection(db, "gifts"), orderBy("level", "asc"));
            const snapshot = await getDocs(q);
            
            const result = [];
            snapshot.forEach((doc) => {
                result.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            setGiftsList(result);
        } catch (e) {
            console.error("Error loading gifts:", e);
        }
    }

    // --- ADD GIFT TO FIRESTORE ---
    async function addGift() {
        if (!newGift.name.trim()) {
            alert("Please enter a gift name");
            return;
        }

        try {
            await addDoc(collection(db, "gifts"), {
                name: newGift.name.trim(),
                level: parseInt(newGift.level) || 1,
                description: newGift.description.trim() || "",
                createdAt: new Date().toISOString()
            });

            setNewGift({
                name: "",
                level: 1,
                description: ""
            });
            setIsAddGift(false);
            loadGifts();
        } catch (e) {
            console.error("Error adding gift:", e);
            alert("Failed to add gift. Please try again.");
        }
    }

    // --- EDIT GIFT IN FIRESTORE ---
    async function updateGift() {
        if (!newGift.name.trim()) {
            alert("Please enter a gift name");
            return;
        }

        try {
            const giftRef = doc(db, "gifts", editingGiftId);
            await updateDoc(giftRef, {
                name: newGift.name.trim(),
                level: parseInt(newGift.level) || 1,
                description: newGift.description.trim() || "",
                updatedAt: new Date().toISOString()
            });

            setNewGift({
                name: "",
                level: 1,
                description: ""
            });
            setEditingGiftId(null);
            setIsAddGift(false);
            loadGifts();
        } catch (e) {
            console.error("Error updating gift:", e);
            alert("Failed to update gift. Please try again.");
        }
    }

    // --- OPEN EDIT FORM ---
    function openEditForm(gift) {
        setEditingGiftId(gift.id);
        setNewGift({
            name: gift.name || "",
            level: gift.level || 1,
            description: gift.description || ""
        });
        setIsAddGift(true);
    }

    // --- CANCEL FORM ---
    function cancelForm() {
        setIsAddGift(false);
        setEditingGiftId(null);
        setNewGift({
            name: "",
            level: 1,
            description: ""
        });
    }

    // --- DUPLICATE GIFT IN FIRESTORE ---
    async function duplicateGift(gift) {
        try {
            await addDoc(collection(db, "gifts"), {
                name: `${gift.name} (Copy)`,
                level: gift.level || 1,
                description: gift.description || "",
                createdAt: new Date().toISOString()
            });
            loadGifts();
        } catch (e) {
            console.error("Error duplicating gift:", e);
            alert("Failed to duplicate gift. Please try again.");
        }
    }

    // --- REMOVE GIFT FROM FIRESTORE ---
    async function removeGift(giftId) {
        if (window.confirm("Are you sure you want to remove this gift?")) {
            try {
                await deleteDoc(doc(db, "gifts", giftId));
                loadGifts();
            } catch (e) {
                console.error("Error removing gift:", e);
                alert("Failed to remove gift. Please try again.");
            }
        }
    }

    // --- GROUP GIFTS BY LEVEL ---
    const giftsByLevel = giftsList.reduce((acc, gift) => {
        const level = gift.level || 1;
        if (!acc[level]) {
            acc[level] = [];
        }
        acc[level].push(gift);
        return acc;
    }, {});

    const levels = Object.keys(giftsByLevel).map(Number).sort((a, b) => a - b);

    // --- GET LOGO FOR GIFT ---
    function getGiftLogo(giftName) {
        const name = giftName.toLowerCase();
        if (name.includes('bolt') || name.includes('bolt printing')) {
            return boltLogo;
        }
        if (name.includes('mounersh') || name.includes('mounersh3d') || name.includes('mounersh 3d')) {
            return mounershLogo;
        }
        return null;
    }

    return (
        <div className={style.page}>
            <div className={style.imgbg}/>
            <div className={style.bg}/>

            {/* Main Screen */}
            <div className={`${style.container} ${isAddGift ? style.fadeOut : style.fadeIn}`}>
                <div className={style.logos}>
                    <img className={style.logoEn} src={logoEn} alt="Logo EN" />
                    <img className={style.logoAr} src={logoAr} alt="Logo AR" />
                </div>

                <p className={style.title}>Gifts</p>

                <div className={style.giftsList}>
                    {giftsList.length === 0 && (
                        <p className={style.noGifts}>No gifts added yet.</p>
                    )}

                    {levels.map((level) => (
                        <div key={level} className={style.levelSection}>
                            <h3 className={style.levelTitle}>Level {level}</h3>
                            <div className={style.giftsGrid}>
                                {giftsByLevel[level].map((gift) => {
                                    const giftLogo = getGiftLogo(gift.name);
                                    return (
                                    <div key={gift.id} className={style.giftItem}>
                                        {giftLogo && (
                                            <img src={giftLogo} alt={gift.name} className={style.giftImage} />
                                        )}
                                        <div className={style.giftContent}>
                                            <span className={style.giftName}>{gift.name}</span>
                                            {gift.description && (
                                                <span className={style.giftDescription}>{gift.description}</span>
                                            )}
                                        </div>
                                        <div className={style.giftActions}>
                                            <button 
                                                className={style.duplicateBtn}
                                                onClick={() => duplicateGift(gift)}
                                                title="Duplicate gift"
                                            >
                                                ⧉
                                            </button>
                                            <button 
                                                className={style.editBtn}
                                                onClick={() => openEditForm(gift)}
                                                title="Edit gift"
                                            >
                                                ✎
                                            </button>
                                            <button 
                                                className={style.removeBtn}
                                                onClick={() => removeGift(gift.id)}
                                                title="Remove gift"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <button onClick={() => setIsAddGift(true)}>Add Gift</button>
            </div>

            {/* Add/Edit Gift Form */}
            <div className={`${style.addGiftContainer} ${isAddGift ? style.fadeIn : style.fadeOut}`}>
                <div className={style.closeBtn} onClick={cancelForm}>X</div>

                <p className={style.title}>{editingGiftId ? "Edit Gift" : "Add Gift"}</p>

                <div className={style.form}>
                    <div className={style.formGroup}>
                        <label>Gift Name</label>
                        <input
                            type="text"
                            value={newGift.name}
                            onChange={(e) => setNewGift({ ...newGift, name: e.target.value })}
                            placeholder="Enter gift name"
                            className={style.input}
                        />
                    </div>

                    <div className={style.formGroup}>
                        <label>Level</label>
                        <input
                            type="number"
                            value={newGift.level}
                            onChange={(e) => setNewGift({ ...newGift, level: parseInt(e.target.value) || 1 })}
                            min="1"
                            placeholder="Enter level"
                            className={style.input}
                        />
                    </div>

                    <div className={style.formGroup}>
                        <label>Description (Optional)</label>
                        <textarea
                            value={newGift.description}
                            onChange={(e) => setNewGift({ ...newGift, description: e.target.value })}
                            placeholder="Enter description"
                            className={style.textarea}
                            rows="3"
                        />
                    </div>

                    <button 
                        onClick={editingGiftId ? updateGift : addGift} 
                        className={style.submitBtn}
                    >
                        {editingGiftId ? "Update Gift" : "Add Gift"}
                    </button>
                    <button onClick={cancelForm} className={style.cancelBtn}>Cancel</button>
                </div>
            </div>
        </div>
    );
}

export default GiftsPage;

